import {
  ArrowDownIcon,
  ArrowUpIcon,
  Button,
  ControlIcon,
  EmptyState,
  ExclamationmarkIcon,
  GenerateIcon,
  Grid,
  GridRow,
  LazyLoader,
  Modal,
} from '@bp/ui-components';
import { observer } from 'mobx-react-lite';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { priorityToSeverity, severityToken } from '../../../utils/timetable/severity';
import { groupWarningsBySeverity } from '../../../utils/timetable/warningGroups';
import { InvalidationBanner } from '../FindingsPanel/InvalidationBanner';
import { ReadinessSummary } from '../FindingsPanel/ReadinessSummary';
import { TestWarningGroup } from '../TestWarningGroup/TestWarningGroup';
import { useTimetableStore } from '../TimetableProvider';
import styles from './TestModal.module.scss';

export const TestModal: FC = observer(() => {
  const { t } = useTranslation();
  const pinboardStore = useTimetableStore();

  const result = pinboardStore.testModal.result;
  const hasWarnings = result && result.length > 0;
  const fromGenerator = pinboardStore.testModal.fromGenerator;

  const handleSendToGenerator = () => {
    void pinboardStore.solverStore.setSolverWizardOpen(true);
    void pinboardStore.setTestModalOpen(false);
    void pinboardStore.setTestModalFromGenerator(false);
  };

  const groupedWarnings = result ? groupWarningsBySeverity(result) : [];

  const summaryCounts = (result ?? []).reduce(
    (acc, warning) => {
      acc[priorityToSeverity(warning.priority)] += 1;
      return acc;
    },
    { error: 0, warning: 0, info: 0 },
  );

  // P0-10: an empty backend result is only "all clear" if there was anything to test. Without cards or a
  // time grid the test returns [] for lack of data — surface that instead of a green success.
  const noTestableData =
    pinboardStore.minimalCards.length === 0 ||
    (pinboardStore.timeGridEntries ?? []).filter((entry) => !entry.pause).length === 0;

  return (
    <Modal
      className={styles['test-modal']}
      isOpen={pinboardStore.testModal.isOpen}
      onRequestClose={() => {
        void pinboardStore.setTestModalFromGenerator(false);
        void pinboardStore.setTestModalOpen(false);
      }}
      title={t('pinboard.test.testAll')}
      parent={document.fullscreenElement ? document.getElementById('timetable-grid') : null}
      actions={
        <div className={styles.actions}>
          <Button
            hierarchy='tertiary'
            icon={<ArrowDownIcon />}
            onClick={() => pinboardStore.expandControlResults(true)}
          >
            {t('pinboard.test.expandAll')}
          </Button>
          <Button hierarchy='tertiary' icon={<ArrowUpIcon />} onClick={() => pinboardStore.expandControlResults(false)}>
            {t('pinboard.test.collapseAll')}
          </Button>
        </div>
      }
      hideFooter={false}
      footerClasses={styles.footer}
      footer={
        <>
          {fromGenerator && hasWarnings && (
            <>
              <div className={styles.warning}>
                <ExclamationmarkIcon />
                <div>{t('pinboard.test.sendToGeneratorWarning')}</div>
              </div>
              <div className={styles.send}>
                <Button hierarchy='primary' icon={<GenerateIcon />} onClick={handleSendToGenerator}>
                  {t('pinboard.test.sendToGeneratorAnyway')}
                </Button>
              </div>
            </>
          )}
        </>
      }
    >
      <Grid>
        {pinboardStore.testModal.isLoading ? (
          <EmptyState
            title={t('pinboard.test.loadingHint')}
            icon={<LazyLoader size='xxl' embedded />}
            iconColor='var(--color-grey-dark)'
            forcedHeight='60vh'
          />
        ) : (
          <>
            {pinboardStore.areFindingsStale('test') && (
              <InvalidationBanner
                onRerun={() => void pinboardStore.runTestCheck()}
                isLoading={pinboardStore.testModal.isLoading}
              />
            )}
            {hasWarnings && (
              <ReadinessSummary
                blockers={summaryCounts.error}
                warnings={summaryCounts.warning}
                hints={summaryCounts.info}
              />
            )}
            <GridRow headline={t('availability.title', { count: 2 })} direction='column' spacingBottom='l'>
              {!hasWarnings ? (
                noTestableData ? (
                  <EmptyState
                    size='small'
                    subtitle={t('pinboard.test.noData')}
                    icon={<ExclamationmarkIcon />}
                    iconColor={severityToken('warning').color}
                  />
                ) : (
                  <EmptyState
                    size='small'
                    subtitle={t('pinboard.test.noWarnings', { type: t('availability.title', { count: 2 }) })}
                    icon={<ControlIcon />}
                    iconColor='var(--color-success)'
                  />
                )
              ) : (
                <>
                  {groupedWarnings?.map(([type, warnings]) => (
                    <TestWarningGroup key={type} type={type} warnings={warnings} />
                  ))}
                </>
              )}
            </GridRow>
          </>
        )}
      </Grid>
    </Modal>
  );
});
